/**
 * Estimate API Service - RTK Query
 * BuilderBel Construction Project Management System
 *
 * Pre-Construction module API endpoints for estimates, line items, and markups.
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  Estimate,
  EstimateLineItem,
  EstimateMarkup,
  EstimateSummary,
  EstimateComparison,
  EstimateListResponse,
  LineItemListResponse,
  EstimateFilterParams,
  LineItemFilterParams,
  CreateEstimateRequest,
  UpdateEstimateRequest,
  CreateLineItemRequest,
  UpdateLineItemRequest,
  CreateMarkupRequest,
  UpdateMarkupRequest,
  SubmitForReviewRequest,
  ApproveEstimateRequest,
  RejectEstimateRequest,
  CompareEstimatesRequest,
} from '../types/estimate';

/**
 * Estimate API Endpoints
 */
export const estimateApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ========================================================================
    // ESTIMATE CRUD
    // ========================================================================

    /**
     * Get all estimates for a project
     */
    getEstimates: builder.query<
      ApiResponse<EstimateListResponse>,
      { projectId: string; filters?: EstimateFilterParams }
    >({
      query: ({ projectId, filters }) => ({
        url: `/projects/${projectId}/estimates`,
        params: filters,
      }),
      providesTags: (result, _error, { projectId }) =>
        result?.data?.estimates
          ? [
              ...result.data.estimates.map(({ _id }) => ({
                type: 'Estimate' as const,
                id: _id,
              })),
              { type: 'Estimate', id: `LIST_${projectId}` },
            ]
          : [{ type: 'Estimate', id: `LIST_${projectId}` }],
    }),

    /**
     * Get a single estimate by ID
     */
    getEstimate: builder.query<ApiResponse<Estimate>, { projectId: string; estimateId: string }>({
      query: ({ projectId, estimateId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}`,
      }),
      providesTags: (_result, _error, { estimateId }) => [{ type: 'Estimate', id: estimateId }],
    }),

    /**
     * Create a new estimate
     */
    createEstimate: builder.mutation<
      ApiResponse<Estimate>,
      { projectId: string; data: CreateEstimateRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/estimates`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),

    /**
     * Update an estimate
     */
    updateEstimate: builder.mutation<
      ApiResponse<Estimate>,
      { projectId: string; estimateId: string; data: UpdateEstimateRequest }
    >({
      query: ({ projectId, estimateId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Delete an estimate (draft only)
     */
    deleteEstimate: builder.mutation<ApiResponse<void>, { projectId: string; estimateId: string }>({
      query: ({ projectId, estimateId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================================================
    // LINE ITEMS
    // ========================================================================

    /**
     * Get all line items for an estimate
     */
    getEstimateLineItems: builder.query<
      ApiResponse<LineItemListResponse>,
      { projectId: string; estimateId: string; filters?: LineItemFilterParams }
    >({
      query: ({ projectId, estimateId, filters }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/line-items`,
        params: filters,
      }),
      providesTags: (result, _error, { estimateId }) =>
        result?.data?.lineItems
          ? [
              ...result.data.lineItems.map(({ _id }) => ({
                type: 'EstimateLineItem' as const,
                id: _id,
              })),
              { type: 'EstimateLineItem', id: `LIST_${estimateId}` },
            ]
          : [{ type: 'EstimateLineItem', id: `LIST_${estimateId}` }],
    }),

    /**
     * Create a line item
     */
    createEstimateLineItem: builder.mutation<
      ApiResponse<EstimateLineItem>,
      { projectId: string; estimateId: string; data: CreateLineItemRequest }
    >({
      query: ({ projectId, estimateId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/line-items`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'EstimateLineItem', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Bulk create line items
     */
    bulkCreateLineItems: builder.mutation<
      ApiResponse<{ created: number; lineItems: EstimateLineItem[] }>,
      { projectId: string; estimateId: string; items: CreateLineItemRequest[] }
    >({
      query: ({ projectId, estimateId, items }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/line-items/bulk`,
        method: 'POST',
        body: { items },
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'EstimateLineItem', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Update a line item
     */
    updateEstimateLineItem: builder.mutation<
      ApiResponse<EstimateLineItem>,
      {
        projectId: string;
        estimateId: string;
        lineItemId: string;
        data: UpdateLineItemRequest;
      }
    >({
      query: ({ projectId, estimateId, lineItemId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/line-items/${lineItemId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId, lineItemId }) => [
        { type: 'EstimateLineItem', id: lineItemId },
        { type: 'EstimateLineItem', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
        { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
      ],
    }),

    /**
     * Delete a line item
     */
    deleteEstimateLineItem: builder.mutation<
      ApiResponse<void>,
      { projectId: string; estimateId: string; lineItemId: string }
    >({
      query: ({ projectId, estimateId, lineItemId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId, lineItemId }) => [
        { type: 'EstimateLineItem', id: lineItemId },
        { type: 'EstimateLineItem', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
        { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
      ],
    }),

    // ========================================================================
    // MARKUPS
    // ========================================================================

    /**
     * Get all markups for an estimate
     */
    getMarkups: builder.query<
      ApiResponse<EstimateMarkup[]>,
      { projectId: string; estimateId: string }
    >({
      query: ({ projectId, estimateId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/markups`,
      }),
      providesTags: (result, _error, { estimateId }) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: 'EstimateMarkup' as const,
                id: _id,
              })),
              { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
            ]
          : [{ type: 'EstimateMarkup', id: `LIST_${estimateId}` }],
    }),

    /**
     * Create a markup
     */
    createMarkup: builder.mutation<
      ApiResponse<EstimateMarkup>,
      { projectId: string; estimateId: string; data: CreateMarkupRequest }
    >({
      query: ({ projectId, estimateId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/markups`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Update a markup
     */
    updateMarkup: builder.mutation<
      ApiResponse<EstimateMarkup>,
      {
        projectId: string;
        estimateId: string;
        markupId: string;
        data: UpdateMarkupRequest;
      }
    >({
      query: ({ projectId, estimateId, markupId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/markups/${markupId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId, markupId }) => [
        { type: 'EstimateMarkup', id: markupId },
        { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Delete a markup
     */
    deleteMarkup: builder.mutation<
      ApiResponse<void>,
      { projectId: string; estimateId: string; markupId: string }
    >({
      query: ({ projectId, estimateId, markupId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/markups/${markupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId, markupId }) => [
        { type: 'EstimateMarkup', id: markupId },
        { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Recalculate all markups
     */
    recalculateMarkups: builder.mutation<
      ApiResponse<{ markups: EstimateMarkup[]; estimate: Estimate }>,
      { projectId: string; estimateId: string }
    >({
      query: ({ projectId, estimateId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/markups/recalculate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'EstimateMarkup', id: `LIST_${estimateId}` },
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    // ========================================================================
    // WORKFLOW
    // ========================================================================

    /**
     * Submit estimate for review
     */
    submitEstimateForReview: builder.mutation<
      ApiResponse<Estimate>,
      { projectId: string; estimateId: string; data?: SubmitForReviewRequest }
    >({
      query: ({ projectId, estimateId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/submit-for-review`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),

    /**
     * Approve estimate
     */
    approveEstimate: builder.mutation<
      ApiResponse<Estimate>,
      { projectId: string; estimateId: string; data?: ApproveEstimateRequest }
    >({
      query: ({ projectId, estimateId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/approve`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),

    /**
     * Reject estimate
     */
    rejectEstimate: builder.mutation<
      ApiResponse<Estimate>,
      { projectId: string; estimateId: string; data: RejectEstimateRequest }
    >({
      query: ({ projectId, estimateId, data }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),

    /**
     * Create a new version of an estimate
     */
    createEstimateVersion: builder.mutation<
      ApiResponse<Estimate>,
      { projectId: string; estimateId: string }
    >({
      query: ({ projectId, estimateId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/create-version`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId, estimateId }) => [
        { type: 'Estimate', id: estimateId },
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================================================
    // REPORTS & ANALYTICS
    // ========================================================================

    /**
     * Get estimate summary with breakdowns
     */
    getEstimateSummary: builder.query<
      ApiResponse<EstimateSummary>,
      { projectId: string; estimateId: string }
    >({
      query: ({ projectId, estimateId }) => ({
        url: `/projects/${projectId}/estimates/${estimateId}/summary`,
      }),
      providesTags: (_result, _error, { estimateId }) => [
        { type: 'EstimateSummary', id: estimateId },
      ],
    }),

    /**
     * Compare multiple estimates
     */
    compareEstimates: builder.mutation<
      ApiResponse<EstimateComparison>,
      { projectId: string; data: CompareEstimatesRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/estimates/compare`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // Estimate CRUD
  useGetEstimatesQuery,
  useLazyGetEstimatesQuery,
  useGetEstimateQuery,
  useLazyGetEstimateQuery,
  useCreateEstimateMutation,
  useUpdateEstimateMutation,
  useDeleteEstimateMutation,

  // Line Items
  useGetEstimateLineItemsQuery,
  useLazyGetEstimateLineItemsQuery,
  useCreateEstimateLineItemMutation,
  useBulkCreateLineItemsMutation,
  useUpdateEstimateLineItemMutation,
  useDeleteEstimateLineItemMutation,

  // Markups
  useGetMarkupsQuery,
  useLazyGetMarkupsQuery,
  useCreateMarkupMutation,
  useUpdateMarkupMutation,
  useDeleteMarkupMutation,
  useRecalculateMarkupsMutation,

  // Workflow
  useSubmitEstimateForReviewMutation,
  useApproveEstimateMutation,
  useRejectEstimateMutation,
  useCreateEstimateVersionMutation,

  // Reports
  useGetEstimateSummaryQuery,
  useLazyGetEstimateSummaryQuery,
  useCompareEstimatesMutation,
} = estimateApi;
